'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher } from 'react-router';
import type { ShopperProducts } from '@/scapi';
import type { BurgerMenuNavigationLoaderData } from './burger-menu-loader-data';
import { parseCategorySubmenuSlots, type CategorySubmenuSlots } from './category-submenu-slots';
import {
    hydrateEmbeddedComponentData,
    type SerializableEmbeddedComponent,
} from './embedded-component';
import {
    GLOBAL_BURGER_MENU_REGION_KEYS,
    type GlobalBurgerMenuRegionKey,
} from './global-burger-menu';
import type { ComponentWithComponentData } from '@/lib/page-designer/component-loader.server';
import { parseMenuItemListOverrides, type MenuItemListOverrides } from './menu-item-list-overrides';

export const BURGER_MENU_NAVIGATION_RESOURCE = '/resource/burger-menu-navigation';

export type HydratedBurgerMenuRegions = Record<
    GlobalBurgerMenuRegionKey,
    ComponentWithComponentData | undefined
>;

type UseBurgerMenuContentResult = {
    subCategoriesPromise: Promise<ShopperProducts.schemas['Category'][]> | undefined;
    burgerMenuRegions: HydratedBurgerMenuRegions;
    menuOverrides: MenuItemListOverrides;
    categorySubmenuSlots: CategorySubmenuSlots;
    isMenuContentLoading: boolean;
    hasLoaded: boolean;
};

function hydrateFromFetcher(
    serializable: SerializableEmbeddedComponent | null | undefined
): ComponentWithComponentData | undefined {
    return serializable ? hydrateEmbeddedComponentData(serializable) : undefined;
}

function emptyRegions(): HydratedBurgerMenuRegions {
    return Object.fromEntries(GLOBAL_BURGER_MENU_REGION_KEYS.map((key) => [key, undefined])) as HydratedBurgerMenuRegions;
}

export function useBurgerMenuContent(open: boolean): UseBurgerMenuContentResult {
    const fetcher = useFetcher<BurgerMenuNavigationLoaderData>({ key: 'burger-menu-navigation' });
    const subsDeferredResolveRef = useRef<((subs: ShopperProducts.schemas['Category'][]) => void) | null>(null);
    const [subCategoriesPromise, setSubCategoriesPromise] = useState<
        Promise<ShopperProducts.schemas['Category'][]> | undefined
    >();

    useEffect(() => {
        if (!open || fetcher.data || fetcher.state !== 'idle') {
            return;
        }

        void fetcher.load(BURGER_MENU_NAVIGATION_RESOURCE);
    }, [open, fetcher, fetcher.data, fetcher.state, fetcher.load]);

    useEffect(() => {
        if (fetcher.data?.subs && subsDeferredResolveRef.current) {
            subsDeferredResolveRef.current(fetcher.data.subs);
            subsDeferredResolveRef.current = null;
        }
    }, [fetcher.data?.subs]);

    useEffect(() => {
        if (!open) {
            setSubCategoriesPromise(undefined);
            subsDeferredResolveRef.current = null;
            return;
        }

        if (fetcher.data?.subs) {
            setSubCategoriesPromise(Promise.resolve(fetcher.data.subs));
            subsDeferredResolveRef.current = null;
            return;
        }

        setSubCategoriesPromise(
            new Promise<ShopperProducts.schemas['Category'][]>((resolve) => {
                subsDeferredResolveRef.current = resolve;
            })
        );
    }, [open, fetcher.data?.subs]);

    const isMenuContentLoading = open && !fetcher.data;

    const burgerMenuRegions = useMemo(() => {
        if (!fetcher.data?.burgerMenuRegions) {
            return emptyRegions();
        }

        return Object.fromEntries(
            GLOBAL_BURGER_MENU_REGION_KEYS.map((key) => [
                key,
                hydrateFromFetcher(fetcher.data?.burgerMenuRegions[key]),
            ])
        ) as HydratedBurgerMenuRegions;
    }, [fetcher.data?.burgerMenuRegions]);

    const menuOverrides = useMemo(
        () =>
            burgerMenuRegions.menuItemLists
                ? parseMenuItemListOverrides(burgerMenuRegions.menuItemLists)
                : {},
        [burgerMenuRegions.menuItemLists]
    );

    const categorySubmenuSlots = useMemo(
        () =>
            burgerMenuRegions.categorySubmenuSlots
                ? parseCategorySubmenuSlots(burgerMenuRegions.categorySubmenuSlots)
                : {},
        [burgerMenuRegions.categorySubmenuSlots]
    );

    return useMemo(
        () => ({
            subCategoriesPromise,
            burgerMenuRegions,
            menuOverrides,
            categorySubmenuSlots,
            isMenuContentLoading,
            hasLoaded: Boolean(fetcher.data),
        }),
        [
            subCategoriesPromise,
            burgerMenuRegions,
            menuOverrides,
            categorySubmenuSlots,
            isMenuContentLoading,
            fetcher.data,
        ]
    );
}
